import React, { useCallback, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { FaceMesh } from '@mediapipe/face_mesh';
import { Face, Vector } from 'kalidokit';
import { useRecoilState, useSetRecoilState, useRecoilValue } from 'recoil';
import { webcamAtom, myFaceAtom, isFaceDetectReadyAtom, cameraInputAtom } from '../../State';
import isElectron from '../../Global';

const FaceDetect = forwardRef(({ mediaStream }, ref) => {
  //Recoil系
  const [webcamState, setWebCamState] = useRecoilState(webcamAtom); //カメラのON/OFF状態
  const [isFaceDetectReady, setIsFaceDetectReady] = useRecoilState(isFaceDetectReadyAtom);
  const setMyFaceState = useSetRecoilState(myFaceAtom); //顔認識結果の送信

  //その他
  const webcamRef = useRef(null); //ウェブカメラの映像を写すvideoタグ
  const faceMeshRef = useRef(null); //フェイスメッシュ
  const intervalRef = useRef(null); //インターバル処理用
  const cameraWidth = 400; //この値が小さい方が処理は軽い
  const FPSValue = 25; //100で 10FPS, 50で20FPS

  //親から呼ばれる
  useImperativeHandle(ref, () => ({
    //再アクティベイト
    activateCameraAgain() {
      //インターバル削除
      if (intervalRef.current != null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      //フェイスメッシュクリア
      if (faceMeshRef.current != null) {
        faceMeshRef.current.close();
        faceMeshRef.current = null;
      }

      //カメラを再度起動
      enableWebCamera();
    },
  }));
  // useScript('https://cubism.live2d.com/sdk-web/cubismcore/live2dcubismcore.min.js');
  // useScript('https://cdn.jsdelivr.net/gh/dylanNew/live2d/webgl/Live2D/lib/live2d.min.js');

  //Webカメラの動作をストップ
  useEffect(() => {
    //WebカメラをOFFにする
    if (webcamState == false) {
      //一時的にカメラをオフにする
      temporalyDisableWebCamera();
    }
    //Webカメラをオンにする
    else {
      enableWebCamera();
    }

    //アンマウント時
    //return () => disableWebCamera();
  }, [webcamState]);

  useEffect(() => {
    //アンマウント時
    return () => disableWebCamera();
  }, []);

  //WebカメラをON
  async function enableWebCamera() {
    //すでにフェイス認識が始まっている場合
    if (mediaStream.current != null && faceMeshRef.current != null) {
      console.log('こちら１');
      //インターバル開始
      if (intervalRef.current == null) {
        intervalRef.current = setInterval(() => {
          //認識処理を渡す
          faceMeshRef.current.send({ image: webcamRef.current });
        }, FPSValue);
      }
    }
    //初回読み込みの場合
    else {
      if (mediaStream != null) {
        //ビデオにストリームを流す
        webcamRef.current.srcObject = mediaStream.current;

        webcamRef.current.onloadedmetadata = async function (e) {
          //ビデオ（カメラ）再生開始
          if (webcamRef.current != null) {
            webcamRef.current.play();

            //顔認識開始
            startFaceDetect();
          }
        };
      }
    }
  }

  //顔認識開始
  async function startFaceDetect() {
    //フェイスメッシュ処理開始
    faceMeshRef.current = new FaceMesh({
      locateFile: (file) => {
        if (isElectron) {
          //ローカルから呼び出す方法の参考 : https://github.com/google/mediapipe/issues/1812
          return `./face_mesh/${file}`;
        } else if (window.location.hostname == 'localhost') {
          return `/face_mesh/${file}`;
        } else {
          return `//cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`;
        }
      },
    });

    faceMeshRef.current.setOptions({
      maxNumFaces: 1,
      refineLandmarks: true, // landmarks 468 -> 478
      minDetectionConfidence: 0.5,
      minTrackingConfidence: 0.5,
      enableFaceGeometry: false,
    });

    //処理結果を渡す
    faceMeshRef.current.onResults(onResults);

    //ここでイニシャライズしないと初めてfaceMesh.sendするときに初期化される
    await faceMeshRef.current.initialize();

    //インターバル開始
    if (intervalRef.current == null) {
      intervalRef.current = setInterval(() => {
        //認識処理を渡す
        if (webcamRef.current != null) {
          faceMeshRef.current.send({ image: webcamRef.current });
        }
      }, FPSValue);
    }
  }

  //WebカメラOFF処理
  function temporalyDisableWebCamera() {
    //タイマー停止
    if (intervalRef.current != null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    //顔認識ローディング中を非表示に
    setIsFaceDetectReady(true);
  }

  //WebカメラOFF処理
  function disableWebCamera(unmount = false) {
    //タイマー停止
    if (intervalRef.current != null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    //フェイスメッシュクリア
    if (faceMeshRef.current != null) {
      faceMeshRef.current.close();
      faceMeshRef.current = null;
    }

    if (webcamRef.current != null) {
      webcamRef.current = null;
    }

    //ビデオと音声を完全にストップ
    if (mediaStream.current != null) {
      //ビデオ停止
      // const videoTrack = mediaStream.current.getVideoTracks()[0];
      // videoTrack.stop();

      // //音声も停止
      // const audioTrack = mediaStream.current.getAudioTracks()[0];
      // audioTrack.stop();

      if (mediaStream.current != null) {
        let currentVideoTracks = mediaStream.current.getVideoTracks();
        currentVideoTracks.forEach((currentVideo) => {
          currentVideo.stop();
        });

        // let currentAudioTracks = mediaStream.current.getAudioTracks();
        // currentAudioTracks.forEach((currentAudio) => {
        //   currentAudio.stop();
        // });
      }

      //メディアストリームもNullに
      //mediaStream.current = null;
    }
  }

  /** 検出結果（フレーム毎に呼び出される） */
  const onResults = useCallback(
    (results) => {
      //ランドマークがある場合
      if (results.multiFaceLandmarks[0] == null) {
        return;
      }

      //lmにフェイスランドマーク情報を格納
      var lm = results.multiFaceLandmarks[0];

      // 検出結果の格納
      let faceRig = Face.solve(lm, {
        runtime: 'mediapipe',
        video: webcamRef.current,
      });

      //親側に値を渡す
      if (faceRig != null) {
        //顔の角度
        var xD = faceRig.head.degrees.x.toFixed(2);
        var yD = faceRig.head.degrees.y.toFixed(2);
        var zD = faceRig.head.degrees.z.toFixed(2);

        //可動域を制限
        xD = clamp(xD, -30, 30);
        yD = clamp(yD, -30, 30);
        zD = clamp(zD, -20, 20);

        //カメラからの距離の差異をなくすために、目の比率情報を元にする（横を見たときなどの歪みにも対応した２点の必要あり）
        var eyeInnerR = new Vector(lm[133]);
        var eyeInnerL = new Vector(lm[362]);
        var eyeOuterR = new Vector(lm[263]);
        var eyeOuterL = new Vector(lm[130]);

        var ratioWidth = eyeInnerL.distance(eyeInnerR);
        var ratioHeight = eyeOuterL.distance(eyeOuterR);

        const upperInnerLip = new Vector(lm[13]);
        const lowerInnerLip = new Vector(lm[14]);
        const mouthCornerLeft = new Vector(lm[61]);
        const mouthCornerRight = new Vector(lm[291]);
        const mouthOpen = upperInnerLip.distance(lowerInnerLip);
        const mouthWidth = mouthCornerLeft.distance(mouthCornerRight);

        //口の横と縦の開閉率
        let ratioY = mouthOpen / ratioWidth;
        var ratioX = mouthWidth / ratioHeight - 0.5;

        //口の開き具合を制限
        ratioX = clamp(ratioX, -0.06, 0.1);
        //  ratioX = clamp(ratioX, -1, 1);

        if (ratioX == -0.06) {
          ratioX = -1;
        }

        if (ratioX == -0.1) {
          ratioX = 1;
        }

        //口の開き具合を制限
        ratioY = clamp(ratioY, -0.05, 0.6);

        //値を総指針
        //if (sendFlgRef.current) {
        //値を更新
        //props.setFaceValue([xD, yD, zD, ratioX, ratioY]);
        // sendFlgRef.current = false;
        //}

        if (isFaceDetectReady == false) {
          //準備完了通知
          setIsFaceDetectReady(true);
        }

        //目の開き具合

        setMyFaceState([xD, yD, zD, ratioX, ratioY, faceRig.eye.l, faceRig.eye.r, faceRig.head.y]);
      }
    },
    [webcamState],
  );

  //制限を設ける関数
  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  //レンダー処理
  return (
    <>
      <video
        ref={webcamRef}
        autoPlay
        muted
        playsInline
        style={{ visibility: 'hidden', display: 'none' }}
        width={cameraWidth}
        height={cameraWidth}
      />
    </>
  );
});

export default FaceDetect;
